import React, { useState, useEffect, useRef } from 'react';
import AudioDbBarView from './AudioDbBarView';

const AudioLevelDisplay = ({
  stream,
  displayLabel,
  onChange,
}: {
  stream?: MediaStream;
  displayLabel?: boolean;
  onChange?: (value: number) => void;
}) => {
  const [dbLevel, setDbLevel] = useState<number>(-160);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const rafRef = useRef<number>();

  useEffect(() => {
    if (!stream) {
      setDbLevel(-160);
      analyserRef.current = null;
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      return;
    }

    const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    audioContextRef.current = audioContext;
    const source = audioContext.createMediaStreamSource(stream);
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;
    source.connect(analyser);
    analyserRef.current = analyser;

    return () => {
      analyser.disconnect();
      source.disconnect();
      audioContext.close();
      analyserRef.current = null;
      audioContextRef.current = null;
    };
  }, [stream]);

  useEffect(() => {
    const measureDbLevel = () => {
      const analyser = analyserRef.current;
      if (!analyser) return;
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      analyser.getByteFrequencyData(dataArray);
      const values = dataArray.reduce((a, b) => a + b, 0);
      const average = values / dataArray.length;
      const db = average > 0 ? 20 * Math.log10(average / 255) : -160;
      setDbLevel(db);
      onChange?.(db);
      rafRef.current = window.requestAnimationFrame(measureDbLevel);
    };

    measureDbLevel();

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [stream]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      {displayLabel && <div>Current dB Level: {dbLevel.toFixed(2)}</div>}
      <AudioDbBarView value={dbLevel} />
    </div>
  );
};

export default AudioLevelDisplay;
